import { useState } from 'react';
import { ApiError } from '@/api/client';
import { setLimosaVerplicht, type LimosaVerplichtBron } from '@/api/administratie';
import { verplichtBronLabel } from './LimosaBadge';

type Keuze = 'automatisch' | 'ja' | 'nee';

/**
 * "Limosa verplicht" for ONE person (decision 17): automatisch (by that person's
 * own company), ja or nee. Shows the effective value and where it comes from.
 */
export function VerplichtKeuze({
  personId,
  name,
  bron: initial,
  onChanged,
}: {
  personId: number;
  name: string;
  bron: LimosaVerplichtBron | null;
  onChanged: () => void;
}) {
  const [bron, setBron] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keuze: Keuze = bron?.keuze ?? 'automatisch';

  const choose = async (next: Keuze) => {
    if (next === keuze) return;
    setSaving(true);
    setError(null);
    try {
      const res = await setLimosaVerplicht(personId, next);
      setBron(res.effective);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : 'Limosa verplicht bijwerken mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="py-2 flex flex-wrap items-center justify-between gap-2" data-testid="verplicht-keuze">
      <div className="min-w-0">
        <p className="text-sm text-td-navy">{name}</p>
        {bron && (
          <p className="text-xs text-td-muted">
            {bron.verplicht ? 'Verplicht' : 'Niet verplicht'} · {verplichtBronLabel(bron)}
          </p>
        )}
        {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
      </div>
      <label className="text-xs text-td-muted">
        <span className="sr-only">Limosa verplicht voor {name}</span>
        <select
          value={keuze}
          disabled={saving || bron === null}
          onChange={(e) => void choose(e.target.value as Keuze)}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-td-navy bg-white"
        >
          <option value="automatisch">Automatisch</option>
          <option value="ja">Ja</option>
          <option value="nee">Nee</option>
        </select>
      </label>
    </li>
  );
}
